import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Phone, Search } from "lucide-react";
import { LOCATIONS, locationPhone } from "../lib/content";
import { useHours } from "../hooks/useHours";

export const todayISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

export const BookingWidget = ({ variant = "hero", slug: sharedSlug, onSlugChange }) => {
  const navigate = useNavigate();
  const { locations } = useHours();
  const [ownSlug, setOwnSlug] = useState("west-branch");
  const slug = sharedSlug ?? ownSlug;
  const setSlug = onSlugChange ?? setOwnSlug;
  const [date, setDate] = useState(todayISO());
  const [error, setError] = useState("");

  // The BFF's location registry decides which marinas can be booked online. Until
  // it answers, fall back to the site registry so the control never renders as
  // phone-only for a location that is in fact live.
  const bookable = (s) =>
    locations ? Boolean(locations.find((l) => l.slug === s)?.enabled) : Boolean(LOCATIONS[s]?.onlineBooking);

  const min = todayISO();
  const contact = locationPhone((locations || []).find((l) => l.slug === slug), slug);

  const submit = (e) => {
    e.preventDefault();
    if (!date) {
      setError("Please choose a date.");
      return;
    }
    if (date < min) {
      setError("Please choose today or a future date.");
      return;
    }
    setError("");
    navigate(`/boat-rentals/${slug}?date=${date}`);
  };

  const dark = variant === "hero";

  return (
    <form
      onSubmit={submit}
      data-testid="booking-widget"
      aria-label="Find available boats"
      className={`rounded-2xl p-5 sm:p-6 ${
        dark
          ? "bg-white/[0.07] backdrop-blur-xl border border-white/20 shadow-2xl shadow-navy/40"
          : "bg-white border border-sand-border shadow-lg shadow-navy/5"
      }`}
    >
      <p className={`eyebrow ${dark ? "text-white/85" : "text-lake"}`}>Start with your date</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-[1.2fr_1fr_auto] sm:items-end">
        <div>
          <label
            htmlFor="bw-location"
            className={`block text-xs font-bold uppercase tracking-wider ${dark ? "text-white/70" : "text-slate-500"}`}
          >
            Location
          </label>
          <select
            id="bw-location"
            data-testid="booking-widget-location-select"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className={`mt-2 h-12 w-full rounded-xl border px-3 text-sm font-semibold ${
              dark
                ? "border-white/25 bg-navy/70 text-white"
                : "border-sand-border bg-sand text-navy"
            }`}
          >
            <option value="west-branch">West Branch State Park</option>
            <option value="lake-milton" disabled={!bookable("lake-milton")}>
              {bookable("lake-milton")
                ? "Lake Milton State Park"
                : "Lake Milton State Park — call to book"}
            </option>
          </select>
        </div>

        <div>
          <label
            htmlFor="bw-date"
            className={`block text-xs font-bold uppercase tracking-wider ${dark ? "text-white/70" : "text-slate-500"}`}
          >
            Date
          </label>
          <div className="relative mt-2">
            <CalendarDays
              className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${
                dark ? "text-white/60" : "text-slate-400"
              }`}
              aria-hidden="true"
            />
            <input
              id="bw-date"
              type="date"
              data-testid="booking-widget-date-input"
              value={date}
              min={min}
              onChange={(e) => {
                setDate(e.target.value);
                setError("");
              }}
              className={`h-12 w-full rounded-xl border pl-9 pr-3 text-sm font-semibold ${
                dark
                  ? "border-white/25 bg-navy/70 text-white [color-scheme:dark]"
                  : "border-sand-border bg-sand text-navy"
              }`}
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="booking-widget-submit-button"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-ember px-6 font-bold text-white transition-all hover:bg-ember-hover hover:-translate-y-0.5 active:translate-y-0"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          Find Boats
        </button>
      </div>

      {error && (
        <p data-testid="booking-widget-error" role="alert" className="mt-3 text-sm font-semibold text-ember">
          {error}
        </p>
      )}

      {slug === "lake-milton" && !bookable("lake-milton") && (
        <p data-testid="booking-widget-milton-hint" className={`mt-3 text-sm ${dark ? "text-white/80" : "text-slate-600"}`}>
          Lake Milton reservations are taken by phone.{" "}
          <a
            href={LOCATIONS["lake-milton"].phoneHref}
            data-testid="booking-widget-milton-phone"
            className="font-bold text-ember hover:underline"
          >
            Call {LOCATIONS["lake-milton"].phone}
          </a>
        </p>
      )}

      {contact && !(slug === "lake-milton" && !bookable("lake-milton")) && (
        <p
          data-testid="booking-widget-call-line"
          className={`mt-3 flex items-center gap-2 text-xs ${dark ? "text-white/60" : "text-slate-500"}`}
        >
          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
          <span>
            Prefer to talk to someone? Call{" "}
            <a href={contact.href} data-testid="booking-widget-call-link" className="hover:underline">
              {contact.phone}
            </a>
            .
          </span>
        </p>
      )}    </form>
  );
};

export default BookingWidget;
